// The yard walk: every meter on one screen, one save.
//
// Ported from Zanetto Tools, and the shape is the whole feature. Somebody
// walks the yard once with a phone, reads forty machines off their dashboards,
// and presses save once. Forty separate saves is not the same product.
//
// Two things it has to get right:
//
//   * ONE SAVE. Only the boxes that were filled in are sent. A blank is not
//     zero, and a machine nobody got to today must not be recorded as
//     unchanged.
//   * THE REGRESSION CONFIRM. A reading lower than the one on file comes back
//     409 with the list; the person sees exactly which machines and decides.
//     They are how a typo gets corrected, so the answer is never just "no".
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fleet,
  regressionsFrom,
  type BulkMeter,
  type Regression,
} from '../fleetApi';
import { ApiError } from '../api';
import { resourceLabel } from '../lib/resourceGroups';
import { EmptyState, Modal, PageHeader } from '../components/ui';

export function BulkReadingsPage() {
  const [meters, setMeters] = useState<BulkMeter[] | null>(null);
  const [entered, setEntered] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<Regression[] | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await fleet.bulkMeters();
      setMeters(result.meters);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the meters.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Only the boxes somebody actually filled in. A blank is "I did not get to
   * this one", which is a different statement from "it has not moved" — and
   * sending it as either would be a lie in the history.
   */
  const entries = useMemo(
    () =>
      Object.entries(entered)
        .filter(([, raw]) => raw.trim() !== '')
        .map(([meterId, raw]) => ({ meterId, reading: Number(raw) }))
        .filter(({ reading }) => Number.isFinite(reading) && reading >= 0),
    [entered]
  );

  async function save(allowRegression: boolean) {
    setBusy(true);
    setError(null);
    try {
      const result = await fleet.saveReadings(entries, allowRegression);
      setConfirming(null);
      setEntered({});
      setNotice(
        `Saved ${result.saved} reading${result.saved === 1 ? '' : 's'}.` +
          (result.regressions.length > 0
            ? result.regressions.length === 1
              ? ' One went backwards and was recorded as entered.'
              : ` ${result.regressions.length} went backwards and were recorded as entered.`
            : '')
      );
      await load();
    } catch (err) {
      const regressions = regressionsFrom(err);
      if (regressions.length > 0) {
        // Not an error yet — a question. The list names each machine, its
        // reading on file and what was just typed, so the person can tell a
        // replaced meter from a fat-fingered digit.
        setConfirming(regressions);
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not save those readings.');
      }
    } finally {
      setBusy(false);
    }
  }

  const byResource = useMemo(() => {
    const groups = new Map<string, BulkMeter[]>();
    for (const meter of meters ?? []) {
      const key = meter.resourceId;
      groups.set(key, [...(groups.get(key) ?? []), meter]);
    }
    return [...groups.values()];
  }, [meters]);

  return (
    <>
      <PageHeader
        title="Enter readings"
        subtitle="Walk the yard, fill in what you can, save once."
        actions={
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={busy || entries.length === 0}
            onClick={() => void save(false)}
          >
            {busy
              ? 'Saving…'
              : entries.length === 0
                ? 'Nothing to save'
                : `Save ${entries.length} reading${entries.length === 1 ? '' : 's'}`}
          </button>
        }
      />

      <p className="mb-4 text-sm">
        <Link to="/app/resources/assets" className="table-link">
          &larr; Back to assets
        </Link>
      </p>

      {error && (
        <div className="banner-error mb-4" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="banner-ok mb-4" role="status">
          {notice}
        </div>
      )}

      {meters !== null && meters.length === 0 ? (
        <EmptyState
          title="No meters yet"
          body="Add a meter to a machine, its hours or its odometer, and it will appear here for the yard walk."
          action={
            <Link to="/app/resources/assets" className="btn-primary btn-sm">
              Go to assets
            </Link>
          }
        />
      ) : (
        <div className="space-y-3">
          {byResource.map((group) => (
            <div
              key={group[0]!.resourceId}
              className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
            >
              <h2 className="mb-3 text-sm font-semibold text-gray-900">
                {resourceLabel({
                  name: group[0]!.resourceName,
                  identifier: group[0]!.resourceIdentifier,
                })}
              </h2>

              <div className="space-y-3">
                {group.map((meter) => (
                  <div key={meter.id} className="flex flex-wrap items-end gap-3">
                    <div className="min-w-0 flex-1">
                      <label className="form-label" htmlFor={`meter-${meter.id}`}>
                        {meter.label}
                        <span className="ml-1 font-normal text-gray-500">({meter.unit})</span>
                      </label>
                      <input
                        id={`meter-${meter.id}`}
                        // `decimal` rather than `number`: it gives a phone the
                        // numeric keypad without the spinner and the
                        // scroll-wheel-changes-the-value trap a number input
                        // brings with it.
                        inputMode="decimal"
                        className="form-input"
                        placeholder={
                          meter.currentReading === null
                            ? 'Never read'
                            : `Last: ${meter.currentReading.toLocaleString('en-AU')}`
                        }
                        value={entered[meter.id] ?? ''}
                        onChange={(e) =>
                          setEntered((prev) => ({ ...prev, [meter.id]: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {confirming && (
        <Modal
          title="Some readings are lower than what is on file"
          onClose={() => setConfirming(null)}
          footer={
            <>
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={() => setConfirming(null)}
              >
                Go back and fix them
              </button>
              <button
                type="button"
                className="btn-primary btn-sm"
                disabled={busy}
                onClick={() => void save(true)}
              >
                {busy ? 'Saving…' : 'Save them anyway'}
              </button>
            </>
          }
        >
          <p className="mb-3 text-sm text-gray-700">
            That usually means a typo. It is also what a replaced meter looks like; if these
            are right, save them and the history will show both.
          </p>
          <ul className="space-y-2 text-sm">
            {confirming.map((r) => (
              <li key={r.meterId} className="rounded border border-amber-300 bg-amber-50 p-3">
                <span className="font-medium text-gray-900">{r.label}</span>
                <div className="text-gray-700">
                  On file {r.current.toLocaleString('en-AU')} {r.unit}; you entered{' '}
                  {r.entered.toLocaleString('en-AU')} {r.unit}
                </div>
              </li>
            ))}
          </ul>
        </Modal>
      )}
    </>
  );
}
